/**
 * Sign-in loop guard for AUTOMATIC redirects (API keeps answering 401, or a token needs interaction):
 * - at most one automatic redirect per 60 seconds in this browser session;
 * - single-flight per MSAL instance: parallel callers share one redirect;
 * - a blocked redirect is reported so the app shows "Your session has ended".
 * - fails closed when sessionStorage is unavailable (no redirect, session-ended page).
 * Manual sign-in ("Log in", "Sign in again") is never blocked.
 */
const KEY = "waulter-admin.autoLoginAt";
export const AUTO_LOGIN_WINDOW_MS = 60_000;

const inFlight = new WeakMap<object, Promise<void>>();

/**
 * True when an automatic redirect may start now; records the attempt.
 * Fails CLOSED: without a working sessionStorage the attempt cannot be recorded, so no redirect.
 */
function tryStartAutoLogin(): boolean {
  try {
    const store = window.sessionStorage;
    const last = Number(store.getItem(KEY) ?? NaN);
    const now = Date.now();
    if (Number.isFinite(last) && now - last < AUTO_LOGIN_WINDOW_MS) return false;
    store.setItem(KEY, String(now));
    return store.getItem(KEY) === String(now);
  } catch {
    return false;
  }
}

export type AutoRedirectOutcome = "started" | "in-flight" | "blocked";

export function startAutoRedirect(owner: object, start: () => Promise<void>): {
  outcome: AutoRedirectOutcome;
  done: Promise<void>;
} {
  const running = inFlight.get(owner);
  if (running) return { outcome: "in-flight", done: running };
  if (!tryStartAutoLogin()) return { outcome: "blocked", done: Promise.resolve() };
  // A rejected redirect (e.g. interaction_in_progress) must not leave the instance "in flight" forever.
  const done = start().catch((error: unknown) => {
    inFlight.delete(owner);
    throw error;
  });
  inFlight.set(owner, done);
  return { outcome: "started", done };
}

export function resetAutoLoginGuard(owner?: object): void {
  if (owner) inFlight.delete(owner);
  try {
    window.sessionStorage.removeItem(KEY);
  } catch {
    // storage unavailable: nothing to reset
  }
}
